/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { memo } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import AiGenerativeCleanupIcon from '@atlaskit/icon-lab/core/ai-generative-cleanup';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { RecommendationBanner } from '../recommendation-banner/RecommendationBanner';
import type { RecommendationBannerVariant } from '../recommendation-banner/types';

import messages from './messages';

const styles = cssMap({
	// Icon tile, per the ROVOFE-3190 Figma spec:
	//   width / height: 40px
	//   border-radius: 8px (== token('radius.medium'))
	//   border: 1px solid token('color.border')
	//   background: token('elevation.surface')
	//
	// We render the tile by hand (rather than using `@atlaskit/icon`'s
	// `IconTile`) because the design's spec values for radius and border
	// don't line up with any single `IconTile` appearance, and stacking
	// `IconTile` inside a styled wrapper produced double-border / mismatched
	// corner artefacts.
	iconTile: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '40px',
		height: '40px',
		flexShrink: 0,
		borderRadius: token('radius.medium'),
		backgroundColor: token('elevation.surface'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
	},
	// Visually upsize the new-core-icon glyph from its `'medium'` (16px)
	// intrinsic size to ~28px so it fills the framed tile proportionally
	// like the Figma glyph does. `base-new` icons only ship `'small'` /
	// `'medium'` size props (no native option to go bigger), and the AFM
	// UI styling standard forbids nested child-selector overrides on the
	// rendered SVG. CSS `transform: scale(...)` on a wrapper achieves the
	// same visual size while keeping styles scoped to a single element
	// the styling lint accepts. The 1.75x scale stays vector-crisp because
	// the underlying SVG has a 0 0 16 viewBox.
	iconScaler: {
		display: 'inline-flex',
		transformOrigin: 'center',
		transform: 'scale(1.75)',
	},
});

// Hoisted to module-level constants so the JSX references are stable across
// renders. Inline `<AiGenerativeCleanupIcon … />` would create a new
// ReactNode each render and defeat `RecommendationBanner`'s `memo()`
// optimisation.
//
// Both variants render the design-system canonical `AiGenerativeCleanup`
// glyph from `@atlaskit/icon-lab` inside the same spec-compliant framed
// tile (see `iconTile` styles above), so fullscreen and sidebar banners
// share the same "icon on a card" treatment.
const ICON_TILE = (
	<Box xcss={styles.iconTile} aria-hidden="true" testId="past-conversations-banner-icon-tile">
		<Box xcss={styles.iconScaler}>
			<AiGenerativeCleanupIcon label="" color={token('color.icon')} />
		</Box>
	</Box>
);

export type PastConversationsBannerProps = {
	/**
	 * Number of recommended past conversations. When `0` the banner renders
	 * nothing so callers can pass the raw value from a recommendations hook
	 * without guarding on it themselves.
	 */
	conversationCount: number;
	/** Invoked when the user presses the "Add chats" CTA. */
	onAddChats: () => void;
	/**
	 * Invoked when the user dismisses the banner. In the `'inline'` variant
	 * this is wired to a `×` icon button; in the `'stacked'` variant it is
	 * wired to a full-width `Dismiss` button below the primary CTA. Omit to
	 * render a non-dismissible banner.
	 */
	onDismiss?: () => void;
	/**
	 * Layout variant passed through to {@link RecommendationBanner}. Defaults
	 * to `'inline'` for the wide Rovo Space landing page. Pass `'stacked'`
	 * when mounting in the narrow Rovo sidebar (ROVOFE-3190).
	 */
	variant?: RecommendationBannerVariant;
	/** Override for the root testId. Defaults to `'past-conversations-banner'`. */
	testId?: string;
};

const PastConversationsBannerComponent = ({
	conversationCount,
	onAddChats,
	onDismiss,
	variant = 'inline',
	testId = 'past-conversations-banner',
}: PastConversationsBannerProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	if (conversationCount <= 0) {
		return null;
	}

	const isStacked = variant === 'stacked';
	const title = formatMessage(isStacked ? messages.sidebarTitle : messages.title);
	const description = formatMessage(messages.description, {
		count: conversationCount,
	});
	const primaryActionLabel = formatMessage(messages.addChatsCta);

	// Spread the dismiss pair conditionally so both keys land together. This
	// keeps the discriminated union on `RecommendationBannerProps` satisfied
	// without duplicating the JSX between dismissible / non-dismissible
	// branches.
	//
	// The stacked variant renders `dismissLabel` as the *visible* button text
	// (per the design — full-width "Dismiss" button below the primary CTA),
	// so we use the shorter `dismissButtonLabel` copy there. The inline
	// variant uses the longer descriptive copy as an accessible name for the
	// icon-only `×` button.
	const dismissProps = onDismiss
		? {
				onDismiss,
				dismissLabel: formatMessage(
					isStacked ? messages.dismissButtonLabel : messages.dismissLabel,
				),
			}
		: {};

	return (
		<RecommendationBanner
			testId={testId}
			icon={ICON_TILE}
			title={title}
			description={description}
			primaryActionLabel={primaryActionLabel}
			onPrimaryAction={onAddChats}
			variant={variant}
			{...dismissProps}
		/>
	);
};

export const PastConversationsBanner: React.MemoExoticComponent<
	(props: PastConversationsBannerProps) => React.JSX.Element | null
> = memo(PastConversationsBannerComponent);
